import { useEffect, useMemo, useState } from 'react';

import { useLocation, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import useLayoutStore from '../../../stores/layoutStore';

import { StyledTabComponent } from './styles';
import { getTabComponents } from './utils';

import { FluidContainer } from '../../../Utils/styles/display';
import { V2_HOME } from '../../../constants/routes';
import useHistoryWrapper from '../../../hooks/useHistoryWrapper';
import { usePopulateFlowDetailsToStore } from '../../../hooks/usePopulateFlowDetailsToStore';
import { useFetchFlowDetailsQuery } from '../../../queries/Flows/Feed';
import { useProfileInfoFetchQuery } from '../../../queries/Profile';
import useFlowBuilderStore from '../../../stores/flowBuilderStore';
import { shareSheetModalOpenSelector } from '../../../stores/flowBuilderStore/selectors';
import FlowsShareSheetController from '../../flows/FlowsShareSheetController';
import { userActivitiesStore } from '../../../stores/userActivitiesStore';
import { FlowAccessRoles } from '../../../queries/Flows/interfaces';
import useGetUserFlowPermissions from '../../../hooks/useGetUserFlowPermissions';

const FlowsBuilderTabsController = () => {
  const isShareSheetModalOpen = useFlowBuilderStore(
    shareSheetModalOpenSelector,
  );

  const history = useHistoryWrapper();
  const location = useLocation<{ flowIdToDuplicate: string }>();

  const { flowId } = useParams<{ flowId: string }>();

  const { data: profileInfo, isFetching: isFetchingProfileInfo } =
    useProfileInfoFetchQuery();
  const slugUrl = `${profileInfo?.assembly.workspaceSlug.name}-${profileInfo?.assembly.workspaceSlug.shortCode}`;

  const { canUserCreateFlow } = useGetUserFlowPermissions();

  const { data: flowDetails, isFetching: isFetchingFlowDetails } =
    useFetchFlowDetailsQuery(flowId || location.state?.flowIdToDuplicate);
  const { populateFlowDetailsFromAPI } = usePopulateFlowDetailsToStore(flowId);
  useEffect(() => {
    populateFlowDetailsFromAPI();
  }, [populateFlowDetailsFromAPI]);

  const isFlowEditor =
    flowDetails?.data.isFlowOwner ||
    Boolean(
      flowDetails?.data.flowAccessRoles?.includes(FlowAccessRoles.Collaborator),
    );

  useEffect(() => {
    if (profileInfo && flowDetails) {
      if (!isFlowEditor && !location.state?.flowIdToDuplicate) {
        history.push(V2_HOME);
      }
    }
  }, [
    profileInfo,
    flowDetails,
    history,
    location.state?.flowIdToDuplicate,
    isFlowEditor,
  ]);

  const { upsertUserActivity } = userActivitiesStore.getState();

  useEffect(() => {
    upsertUserActivity({
      visitCount: 1,
      entityId: flowId,
      lastVisitedAt: new Date().toISOString(),
    });
  }, [flowId, upsertUserActivity]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const { isLeftAsideOpen, toggleLeftAsideOpen } = useLayoutStore(
    (state) => ({
      isLeftAsideOpen: state.isLeftAsideOpen,
      toggleLeftAsideOpen: state.toggleLeftAsideOpen,
    }),
    shallow,
  );

  useEffect(() => {
    if (isLeftAsideOpen && location.pathname.includes('editor')) {
      toggleLeftAsideOpen();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const folderId = useMemo(() => {
    return new URLSearchParams(window.location.search).get('folderId');
  }, []);

  const tabComponentProps = useMemo(() => {
    return getTabComponents({ flowId, folderId, slugUrl, canUserCreateFlow });
  }, [canUserCreateFlow, flowId, folderId, slugUrl]);

  const onSelectTabHandler = (index: number) => {
    setSelectedTabIndex(index);
  };

  return (
    <>
      {isShareSheetModalOpen && <FlowsShareSheetController />}
      {!isFetchingFlowDetails && !isFetchingProfileInfo && (
        <FluidContainer id="sisenseApp">
          <StyledTabComponent
            {...tabComponentProps}
            selectedTabIndex={selectedTabIndex}
            handleSelectTab={onSelectTabHandler}
            isTabPositionFixed
          />
        </FluidContainer>
      )}
    </>
  );
};

export default FlowsBuilderTabsController;
